/**
 * Stripe Connect Status Utilities
 * Check if user has Stripe Connect set up and is ready for payments
 */

/**
 * Check if user has Stripe Connect set up
 * @param {Object} userData - User document data
 * @returns {Object} Status object
 */
export const checkStripeConnectStatus = (userData) => {
  if (!userData) {
    return {
      isSetUp: false,
      isActive: false,
      canReceivePayments: false,
      canMakePayments: false,
      status: 'not_set_up',
      message: 'Stripe Connect not set up'
    };
  }

  const hasAccountId = !!userData.stripeConnectAccountId;
  const isActive = userData.stripeConnectStatus === 'active';
  const chargesEnabled = userData.stripeConnectChargesEnabled === true;
  const payoutsEnabled = userData.stripeConnectPayoutsEnabled === true;
  const isSkipped = userData.stripeConnectSkipped === true;

  if (isSkipped) {
    return {
      isSetUp: false,
      isActive: false,
      canReceivePayments: false,
      canMakePayments: false,
      status: 'skipped',
      message: 'Stripe Connect setup was skipped'
    };
  }

  if (!hasAccountId) {
    return {
      isSetUp: false,
      isActive: false,
      canReceivePayments: false,
      canMakePayments: false,
      status: 'not_set_up',
      message: 'Stripe Connect account not created'
    };
  }

  if (!isActive) {
    return {
      isSetUp: true,
      isActive: false,
      canReceivePayments: false,
      canMakePayments: false,
      status: 'incomplete',
      message: 'Stripe Connect account setup incomplete'
    };
  }

  if (!chargesEnabled) {
    return {
      isSetUp: true,
      isActive: true,
      canReceivePayments: false,
      canMakePayments: false,
      status: 'charges_disabled',
      message: 'Charges not enabled on Stripe Connect account'
    };
  }

  if (!payoutsEnabled) {
    return {
      isSetUp: true,
      isActive: true,
      canReceivePayments: false,
      canMakePayments: true,
      status: 'payouts_disabled',
      message: 'Payouts not enabled on Stripe Connect account'
    };
  }

  return {
    isSetUp: true,
    isActive: true,
    canReceivePayments: true,
    canMakePayments: true,
    status: 'active',
    message: 'Stripe Connect fully set up and active'
  };
};

/**
 * Check if user needs Stripe Connect setup
 * @param {Object} userData - User document data
 * @param {String} userRole - User's role
 * @returns {Boolean} Whether user needs Stripe Connect setup
 */
export const needsStripeConnectSetup = (userData, userRole) => {
  // Admins don't need Stripe Connect
  if (userRole === 'admin') {
    return false;
  }

  // League users don't need Stripe Connect
  if (userRole === 'league_user') {
    return false;
  }

  // Hosts need Stripe Connect for launching games and receiving payments
  if (userRole === 'host') {
    const status = checkStripeConnectStatus(userData);
    return !status.isSetUp || !status.isActive;
  }

  // Default to requiring setup for other roles
  return true;
};

/**
 * Get Stripe Connect setup requirements for user role
 * @param {String} userRole - User's role
 * @returns {Object} Requirements object
 */
export const getStripeConnectRequirements = (userRole) => {
  switch (userRole) {
    case 'admin':
      return {
        required: false,
        reason: 'Admins have full platform access',
        canSkip: true
      };
    
    case 'league_user':
      return {
        required: false,
        reason: 'League users only play games, no payments needed',
        canSkip: true
      };
    
    case 'host':
      return {
        required: true,
        reason: 'Hosts need payments to launch games and receive marketplace revenue',
        canSkip: false
      };
    
    default:
      return {
        required: true,
        reason: 'Payment setup required for full platform access',
        canSkip: true
      };
  }
};

/**
 * Get user-friendly status message
 * @param {Object} status - Status object from checkStripeConnectStatus
 * @returns {String} User-friendly message
 */
export const getStatusMessage = (status) => {
  switch (status.status) {
    case 'not_set_up':
      return 'Payment account not set up';
    
    case 'skipped':
      return 'Payment setup was skipped';
    
    case 'incomplete':
      return 'Payment account setup incomplete';
    
    case 'charges_disabled':
      return 'Payment account needs charges enabled';
    
    case 'payouts_disabled':
      return 'Payment account needs payouts enabled';
    
    case 'active':
      return 'Payment account ready';
    
    default:
      return 'Payment account status unknown';
  }
};

/**
 * Get status badge color
 * @param {Object} status - Status object from checkStripeConnectStatus
 * @returns {String} CSS color class
 */
export const getStatusBadgeColor = (status) => {
  switch (status.status) {
    case 'active':
      return 'bg-green-500 text-green-900';
    
    case 'incomplete':
    case 'charges_disabled':
    case 'payouts_disabled':
      return 'bg-yellow-500 text-yellow-900';
    
    case 'not_set_up':
    case 'skipped':
      return 'bg-red-500 text-red-900';
    
    default:
      return 'bg-gray-500 text-gray-900';
  }
};

/**
 * Get Stripe Connect status from Firebase function
 * @returns {Promise<Object>} Status object
 */
export const getStripeConnectStatusFromFunction = async () => {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const getStripeConnectStatus = httpsCallable(functions, 'getStripeConnectStatus');
    
    const result = await getStripeConnectStatus();
    return result.data;
  } catch (error) {
    console.error('Error getting Stripe Connect status:', error);
    return {
      hasAccount: false,
      status: 'error'
    };
  }
};
